import React, { useMemo, useState } from 'react';
import Table, { CellProps } from 'rsuite/Table';
import { ToggleCell } from '../../common/table/toggle-cell';
import { AlertService } from '../../../services/alert.service';
import Whisper from 'rsuite/Whisper';
import IconButton from 'rsuite/IconButton';
import { WhisperSpeaker } from '../../../utils/whisper-speaker';
import Popover from 'rsuite/Popover';
import Dropdown from 'rsuite/Dropdown';
import { ModalsService } from '../../../services/modals.service';
import { usePaginationQs } from '../../../utils/usePagination';
import Pagination from 'rsuite/Pagination';
import MyLoader from '../../common/loader.component';
import { RouterOutput, trpcSwr } from '../../../api/trpc';
import { AddOtcRecommendation } from './AddOtcRecommendation';
import { ImagesCell } from '../../common/cards/images-cell-card.component';
import Toggle from 'rsuite/Toggle';
import { Checkbox, Drawer, Tooltip } from 'rsuite';
import { fetcher } from '../../../services/api';
import { ConfirmModal } from '../../common/confirm-modal';
import { OtcRecommendationSettings } from './OtcRecommendationSettings';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

export type Item =
  RouterOutput['otcRecommendationAdmin']['list']['items'][number];

const fields = [
  { label: 'Id', value: 'id' },
  { label: 'Артикул', value: 'objectId' },
  { label: 'Фото', value: 'image' },
  { label: 'Название товара', value: 'name' },
  { label: 'Показы', value: 'views' },
  { label: 'Клики', value: 'clicks' },
  { label: 'CTR%', value: 'ctr' },
  { label: 'Вес%', value: 'weight' },
  { label: 'Показы внешние', value: 'publicViews' },
  { label: 'Клики внешние', value: 'publicClicks' },
  { label: 'CTR% внешний ', value: 'publicCtr' },
  { label: 'Вес внешний%', value: 'publicWeight' },
  { label: 'Разгон', value: 'boost' },
  { label: 'Категория', value: 'tags' },
  { label: 'Склад', value: 'stock' },
];

type FieldsForm = {
  fields: (typeof fields)[number]['value'][];
};

export const OtcRecommendationTab = () => {
  const { take, skip, handleSetPage, handleChangeLimit, page } =
    usePaginationQs(10);
  const { trigger: triggerDelete } =
    trpcSwr.otcRecommendationAdmin.delete.useSWRMutation({ fetcher });
  const [directionIdAsc, setDirectionIdAsc] = useState<'asc' | undefined>();
  const [openFieldSettings, setOpenFieldSettings] = useState(false); // Открыть меню для отображения отображаемых полей

  const { data, mutate, isLoading, error } =
    trpcSwr.otcRecommendationAdmin.list.useSWR({
      take,
      skip,
      orderBy: directionIdAsc ? { id: 'asc' } : undefined,
    });

  const hasEnoughBoost = useMemo(() => {
    if (data?.items) {
      const boostNotNullCount = data.items.filter(
        (item) => item.boost !== null,
      ).length;
      return boostNotNullCount < 2;
    }
  }, [data?.items]);

  const { trigger } = trpcSwr.otcRecommendationAdmin.update.useSWRMutation();

  const {
    handleSubmit: handleSubmitFields,
    control: controlFields,
    watch: watchFields,
  } = useForm<FieldsForm>({
    defaultValues: {
      fields: [
        'id',
        'objectId',
        'image',
        'name',
        'views',
        'clicks',
        'ctr',
        'weight',
        'publicViews',
        'publicClicks',
        'publicCtr',
        'publicWeight',
        'boost',
        'tags',
        'stock',
      ],
    },
  });

  const selectedFields = watchFields('fields');

  async function toggleDisabled(rowData: Item, value: boolean) {
    await trigger({ id: rowData.id, data: { disabled: value } });
    await mutate();

    AlertService.success();
  }

  const renderMenu =
    (item: Item, hasEnoughBoost?: boolean): WhisperSpeaker =>
    ({ onClose, left, top, className }, ref) => {
      const handleSelect = (eventKey: number | string | undefined) => {
        onClose();
        if (eventKey === 'edit') {
          ModalsService.createModal(AddOtcRecommendation, {
            item,
            hasEnoughBoost,
          }).then(() => mutate());
        }
        if (eventKey === 'delete') {
          ModalsService.createModal(ConfirmModal, {
            title: 'Удалить рекомендацию?',
            buttonText: `Удалить`,
          })
            .then(() => {
              return triggerDelete({ id: item.id });
            })
            .then(() => {
              AlertService.success();
              void mutate();
            });
        }
      };

      return (
        <Popover ref={ref} className={className} style={{ left, top }} full>
          <Dropdown.Menu onSelect={handleSelect}>
            <Dropdown.Item eventKey={'edit'}>
              <i className="bi bi-pencil" />
              <span className="ps-2">Изменить</span>
            </Dropdown.Item>
            <Dropdown.Item eventKey={'delete'}>
              <i className="bi bi-trash" />
              <span className="ps-2">Удалить</span>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Popover>
      );
    };

  const ActionCell = ({ rowData, dataKey, ...props }: CellProps<Item>) => {
    return (
      rowData && (
        <Table.Cell
          {...props}
          className="link-group no-padding-cell"
          align="right"
        >
          <Whisper
            placement="leftStart"
            trigger="click"
            speaker={renderMenu(rowData, hasEnoughBoost)}
          >
            <IconButton
              appearance="subtle"
              icon={<i className="bi bi-three-dots" />}
            />
          </Whisper>
        </Table.Cell>
      )
    );
  };

  const ItemDeleted = ({ rowData, dataKey, ...props }: CellProps<Item>) => {
    const { data: item } =
      trpcSwr.otcommerce.BatchGetSimplifiedItemFullInfo.useSWR({
        itemId: rowData ? rowData.objectId : '',
      });

    const itemNotFoundTooltip = (
      <Tooltip className="fs-14 text-start lh-sm p-2" style={{ maxWidth: 380 }}>
        {item?.ErrorCode}
      </Tooltip>
    );

    if (item && item.ErrorCode !== 'Ok') {
      return (
        <Table.Cell {...props}>
          <Whisper
            placement="right"
            trigger="hover"
            speaker={itemNotFoundTooltip}
          >
            <i className="bi bi-exclamation-diamond-fill text-danger"></i>
          </Whisper>
        </Table.Cell>
      );
    }

    return rowData && <Table.Cell {...props}></Table.Cell>;
  };

  if (isLoading) {
    return (
      <>
        <div className="pb-4">
          <MyLoader />
        </div>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <div className="pb-4">Неизвестная ошибка</div>
      </>
    );
  }

  const sortHandleChange = () => {
    setDirectionIdAsc(directionIdAsc === 'asc' ? undefined : 'asc');
  };
  return (
    <div>
      <Drawer
        backdrop={true}
        open={openFieldSettings}
        onClose={() => setOpenFieldSettings(false)}
      >
        <Drawer.Header>
          <Drawer.Title>
            <FormattedMessage
              defaultMessage="Поля для отображения"
              id="TransactiosHistrory.title.FieldsToDisplay"
              description="Заголовок фильтра"
            />
          </Drawer.Title>
        </Drawer.Header>
        <Drawer.Body>
          <form
            className="h-100 d-flex flex-column justify-content-between"
            onSubmit={handleSubmitFields(() => setOpenFieldSettings(false))}
          >
            <div className="row">
              {fields.map((field) => (
                <div key={field.value} className="col-6">
                  <Controller
                    name="fields"
                    control={controlFields}
                    render={({ field: { onChange, value } }) => (
                      <Checkbox
                        value={field.value}
                        checked={value?.includes(field.value)}
                        onChange={(checked) => {
                          const newValue = value.includes(checked as string)
                            ? value.filter((v) => v !== field.value)
                            : [...value, field.value];
                          onChange(newValue);
                        }}
                      >
                        {field.label}
                      </Checkbox>
                    )}
                  />
                </div>
              ))}
            </div>
            <button type="submit" className="btn btn-success w-100">
              <FormattedMessage
                defaultMessage="Готово"
                id="TransactiosHistrory.btn.Ready"
                description="Кнопка"
              />
            </button>
          </form>
        </Drawer.Body>
      </Drawer>

      <div className={'d-flex gap-2 p-1'}>
        <div className={'align-content-end'}>
          <button
            className="btn btn-light border"
            onClick={() => setOpenFieldSettings(true)}
          >
            Отображаемые поля ({selectedFields.length}/{fields.length})
          </button>
        </div>
        <OtcRecommendationSettings />
        <div
          className="d-flex align-self-end pb-2"
          style={{ minWidth: '200px' }}
        >
          <div className="d-flex ">
            <Toggle
              onChange={sortHandleChange}
              checked={directionIdAsc === 'asc'}
            />
            <div className="ms-2">Сортировка по ID</div>
          </div>
        </div>
      </div>

      <Table
        loading={isLoading}
        data={data.items}
        headerHeight={57}
        rowHeight={74}
        autoHeight
        rowKey="id"
      >
        {selectedFields.includes('id') && (
          <Table.Column fullText width={50}>
            <Table.HeaderCell>Id</Table.HeaderCell>
            <Table.Cell dataKey="id" />
          </Table.Column>
        )}

        <Table.Column fullText width={50}>
          <Table.HeaderCell> </Table.HeaderCell>
          <ItemDeleted />
        </Table.Column>
        {selectedFields.includes('objectId') && (
          <Table.Column fullText width={200}>
            <Table.HeaderCell>Артикул</Table.HeaderCell>
            <Table.Cell dataKey="objectId" />
          </Table.Column>
        )}

        {selectedFields.includes('image') && (
          <Table.Column fullText>
            <Table.HeaderCell>Фото</Table.HeaderCell>
            <Table.Cell>
              {({ image }: Item) =>
                image ? (
                  <ImagesCell imagesList={[image]} width={48} height={48} />
                ) : null
              }
            </Table.Cell>
          </Table.Column>
        )}

        {selectedFields.includes('name') && (
          <Table.Column fullText width={150}>
            <Table.HeaderCell>Название товара</Table.HeaderCell>
            <Table.Cell dataKey="name" />
          </Table.Column>
        )}

        {selectedFields.includes('views') && (
          <Table.Column fullText width={100}>
            <Table.HeaderCell>Показы</Table.HeaderCell>
            <Table.Cell>
              {({ views }: Item) => (views ? `${views}` : null)}
            </Table.Cell>
          </Table.Column>
        )}
        {selectedFields.includes('clicks') && (
          <Table.Column fullText width={100}>
            <Table.HeaderCell>Клики</Table.HeaderCell>
            <Table.Cell>
              {({ clicks }: Item) => (clicks ? `${clicks}` : null)}
            </Table.Cell>
          </Table.Column>
        )}

        {selectedFields.includes('ctr') && (
          <Table.Column fullText>
            <Table.HeaderCell>CTR%</Table.HeaderCell>
            <Table.Cell>
              {({ ctr }: Item) => (ctr ? (ctr * 100).toFixed(6) : null)}
            </Table.Cell>
          </Table.Column>
        )}

        {selectedFields.includes('weight') && (
          <Table.Column fullText>
            <Table.HeaderCell>Вес%</Table.HeaderCell>
            <Table.Cell>
              {({ weight }: Item) => (weight ? weight.toFixed(6) : null)}
            </Table.Cell>
          </Table.Column>
        )}

        {selectedFields.includes('publicViews') && (
          <Table.Column fullText width={100}>
            <Table.HeaderCell>Показы внешние</Table.HeaderCell>
            <Table.Cell>
              {({ publicViews }: Item) =>
                publicViews ? `${publicViews}` : null
              }
            </Table.Cell>
          </Table.Column>
        )}

        {selectedFields.includes('publicClicks') && (
          <Table.Column fullText width={100}>
            <Table.HeaderCell>Клики внешние</Table.HeaderCell>
            <Table.Cell>
              {({ publicClicks }: Item) =>
                publicClicks ? `${publicClicks}` : null
              }
            </Table.Cell>
          </Table.Column>
        )}

        {selectedFields.includes('publicCtr') && (
          <Table.Column fullText>
            <Table.HeaderCell>CTR% внешний</Table.HeaderCell>
            <Table.Cell>
              {({ publicCtr }: Item) =>
                publicCtr ? (publicCtr * 100).toFixed(6) : null
              }
            </Table.Cell>
          </Table.Column>
        )}

        {selectedFields.includes('publicWeight') && (
          <Table.Column fullText>
            <Table.HeaderCell>Вес% внешний</Table.HeaderCell>
            <Table.Cell>
              {({ publicWeight }: Item) =>
                publicWeight ? publicWeight.toFixed(6) : null
              }
            </Table.Cell>
          </Table.Column>
        )}

        {selectedFields.includes('boost') && (
          <Table.Column fullText>
            <Table.HeaderCell>Разгон</Table.HeaderCell>
            <Table.Cell>
              {({ boost }: Item) => (boost ? `${boost}` : null)}
            </Table.Cell>
          </Table.Column>
        )}

        <Table.Column fullText>
          <Table.HeaderCell>Включен</Table.HeaderCell>
          <ToggleCell
            dataKey="disabled"
            inverted={true}
            onChange={toggleDisabled}
          />
        </Table.Column>

        {selectedFields.includes('tags') && (
          <Table.Column fullText>
            <Table.HeaderCell>Категория</Table.HeaderCell>
            <Table.Cell dataKey="tags" />
          </Table.Column>
        )}

        {selectedFields.includes('stock') && (
          <Table.Column fullText>
            <Table.HeaderCell>Склад</Table.HeaderCell>
            <Table.Cell dataKey="stock" />
          </Table.Column>
        )}

        <Table.Column fullText fixed={'right'}>
          <Table.HeaderCell> </Table.HeaderCell>
          <ActionCell />
        </Table.Column>
      </Table>

      {data.total > take && (
        <Pagination
          className="mt-2 pb-4"
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          size="md"
          layout={['pager']}
          total={data.total}
          limit={take}
          activePage={page}
          onChangePage={handleSetPage}
          onChangeLimit={handleChangeLimit}
        />
      )}
    </div>
  );
};
